import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useSupabase } from './supabase'
import i18n from '@/i18n'

const THEME_STORAGE_KEY = 'gaze-theme-preference'
const MOTION_STORAGE_KEY = 'gaze-motion-preference'
const LANGUAGE_STORAGE_KEY = 'gaze-language-preference'

export const useTheme = defineStore('theme', () => {
  const supabase = useSupabase()
  const isDark = ref(false)
  const isReducedMotion = ref(false)
  const language = ref('en')
  const isInitialized = ref(false)

  // Detect OS dark mode preference
  function detectOSDarkMode(): boolean {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  // Detect OS reduced motion preference
  function detectOSReducedMotion(): boolean {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  // Get theme from localStorage
  function getStoredTheme(): boolean | null {
    if (typeof window === 'undefined') return null
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === null) return null
    return stored === 'dark'
  }

  // Get motion preference from localStorage
  function getStoredMotion(): boolean | null {
    if (typeof window === 'undefined') return null
    const stored = localStorage.getItem(MOTION_STORAGE_KEY)
    if (stored === null) return null
    return stored === 'reduced'
  }

  // Get language from localStorage
  function getStoredLanguage(): string | null {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(LANGUAGE_STORAGE_KEY)
  }

  // Store theme in localStorage
  function storeTheme(dark: boolean) {
    if (typeof window === 'undefined') return
    localStorage.setItem(THEME_STORAGE_KEY, dark ? 'dark' : 'light')
  }

  // Store motion preference in localStorage
  function storeMotion(reduced: boolean) {
    if (typeof window === 'undefined') return
    localStorage.setItem(MOTION_STORAGE_KEY, reduced ? 'reduced' : 'normal')
  }

  // Store language in localStorage
  function storeLanguage(lang: string) {
    if (typeof window === 'undefined') return
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
  }

  // Clear theme from localStorage (for testing)
  function clearStoredTheme() {
    if (typeof window === 'undefined') return
    localStorage.removeItem(THEME_STORAGE_KEY)
  }

  // Clear motion preference from localStorage (for testing)
  function clearStoredMotion() {
    if (typeof window === 'undefined') return
    localStorage.removeItem(MOTION_STORAGE_KEY)
  }

  // Clear language from localStorage (for testing)
  function clearStoredLanguage() {
    if (typeof window === 'undefined') return
    localStorage.removeItem(LANGUAGE_STORAGE_KEY)
  }

  // Initialize theme and motion with priority: localStorage > user preference > OS preference
  function initializeTheme() {
    if (isInitialized.value) return

    // Initialize theme
    const storedTheme = getStoredTheme()
    if (storedTheme !== null) {
      isDark.value = storedTheme
    } else if (supabase.user.status === 'SignedIn' && supabase.user.data.dark_mode !== undefined) {
      isDark.value = supabase.user.data.dark_mode
      storeTheme(isDark.value)
    } else {
      const osDarkMode = detectOSDarkMode()
      isDark.value = osDarkMode
      storeTheme(isDark.value)
    }

    // Initialize motion preference
    const storedMotion = getStoredMotion()
    if (storedMotion !== null) {
      isReducedMotion.value = storedMotion
    } else if (
      supabase.user.status === 'SignedIn' &&
      supabase.user.data.reduced_motion !== undefined
    ) {
      isReducedMotion.value = supabase.user.data.reduced_motion
      storeMotion(isReducedMotion.value)
    } else {
      const osReducedMotion = detectOSReducedMotion()
      isReducedMotion.value = osReducedMotion
      storeMotion(isReducedMotion.value)
    }

    // Initialize language
    const storedLanguage = getStoredLanguage()
    if (storedLanguage !== null) {
      language.value = storedLanguage
    } else if (supabase.user.status === 'SignedIn' && supabase.user.data.language !== undefined) {
      language.value = supabase.user.data.language
      storeLanguage(language.value)
    } else {
      const browserLanguage = navigator.language.split('-')[0]
      if (['en', 'es', 'fr', 'it', 'pt', 'de'].includes(browserLanguage)) {
        language.value = browserLanguage
      }
      storeLanguage(language.value)
    }

    applyTheme()
    applyMotion()
    applyLanguage()
    isInitialized.value = true
  }

  // Apply theme to document
  function applyTheme() {
    if (typeof document === 'undefined') return

    if (isDark.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Apply motion preference to document
  function applyMotion() {
    if (typeof document === 'undefined') return

    if (isReducedMotion.value) {
      document.documentElement.classList.add('motion-reduce')
    } else {
      document.documentElement.classList.remove('motion-reduce')
    }
  }

  // Apply language to i18n
  function applyLanguage() {
    i18n.global.locale.value = language.value as 'de' | 'en' | 'es' | 'fr' | 'it' | 'pt'
  }

  // Toggle theme
  async function toggleTheme() {
    isDark.value = !isDark.value
    applyTheme()
    storeTheme(isDark.value)

    // Save to database if user is signed in
    if (supabase.user.status === 'SignedIn') {
      await supabase.api
        .from('user_settings')
        .update({ dark_mode: isDark.value })
        .eq('user_id', supabase.user.data.id)

      // Update local user data
      await supabase.fetchProfile(supabase.user.data.id)
    }
  }

  // Toggle motion preference
  async function toggleMotion() {
    isReducedMotion.value = !isReducedMotion.value
    applyMotion()
    storeMotion(isReducedMotion.value)

    // Save to database if user is signed in
    if (supabase.user.status === 'SignedIn') {
      await supabase.api
        .from('user_settings')
        .update({ reduced_motion: isReducedMotion.value })
        .eq('user_id', supabase.user.data.id)

      // Update local user data
      await supabase.fetchProfile(supabase.user.data.id)
    }
  }

  // Set theme explicitly
  async function setTheme(dark: boolean) {
    isDark.value = dark
    applyTheme()
    storeTheme(isDark.value)

    // Save to database if user is signed in
    if (supabase.user.status === 'SignedIn') {
      await supabase.api
        .from('user_settings')
        .update({ dark_mode: isDark.value })
        .eq('user_id', supabase.user.data.id)

      // Update local user data
      await supabase.fetchProfile(supabase.user.data.id)
    }
  }

  // Set motion preference explicitly
  async function setMotion(reduced: boolean) {
    isReducedMotion.value = reduced
    applyMotion()
    storeMotion(isReducedMotion.value)

    // Save to database if user is signed in
    if (supabase.user.status === 'SignedIn') {
      await supabase.api
        .from('user_settings')
        .update({ reduced_motion: isReducedMotion.value })
        .eq('user_id', supabase.user.data.id)

      // Update local user data
      await supabase.fetchProfile(supabase.user.data.id)
    }
  }

  // Set language explicitly
  async function setLanguage(lang: string) {
    language.value = lang
    applyLanguage()
    storeLanguage(language.value)

    // Save to database if user is signed in
    if (supabase.user.status === 'SignedIn') {
      await supabase.api
        .from('user_settings')
        .update({ language: language.value })
        .eq('user_id', supabase.user.data.id)

      // Update local user data
      await supabase.fetchProfile(supabase.user.data.id)
    }
  }

  // Sync with database (called when user data changes)
  async function syncWithDatabase() {
    if (supabase.user.status === 'SignedIn') {
      // Sync theme
      if (
        supabase.user.data.dark_mode !== undefined &&
        isDark.value !== supabase.user.data.dark_mode
      ) {
        isDark.value = supabase.user.data.dark_mode
        storeTheme(isDark.value)
        applyTheme()
      }

      // Sync motion preference
      if (
        supabase.user.data.reduced_motion !== undefined &&
        isReducedMotion.value !== supabase.user.data.reduced_motion
      ) {
        isReducedMotion.value = supabase.user.data.reduced_motion
        storeMotion(isReducedMotion.value)
        applyMotion()
      }

      // Sync language
      if (
        supabase.user.data.language !== undefined &&
        language.value !== supabase.user.data.language
      ) {
        language.value = supabase.user.data.language
        storeLanguage(language.value)
        applyLanguage()
      }
    }
  }

  // Watch for user changes to sync theme and motion
  watch(
    () => supabase.user.status,
    () => {
      if (supabase.user.status === 'SignedIn') {
        syncWithDatabase()
      }
    }
  )

  // Watch for dark_mode changes in user data
  watch(
    () => (supabase.user.status === 'SignedIn' ? supabase.user.data.dark_mode : undefined),
    (newDarkMode) => {
      if (newDarkMode !== undefined && supabase.user.status === 'SignedIn') {
        syncWithDatabase()
      }
    }
  )

  // Watch for reduced_motion changes in user data
  watch(
    () => (supabase.user.status === 'SignedIn' ? supabase.user.data.reduced_motion : undefined),
    (newReducedMotion) => {
      if (newReducedMotion !== undefined && supabase.user.status === 'SignedIn') {
        syncWithDatabase()
      }
    }
  )

  // Watch for language changes in user data
  watch(
    () => (supabase.user.status === 'SignedIn' ? supabase.user.data.language : undefined),
    (newLanguage) => {
      if (newLanguage !== undefined && supabase.user.status === 'SignedIn') {
        syncWithDatabase()
      }
    }
  )

  return {
    isDark,
    isReducedMotion,
    language,
    isInitialized,
    initializeTheme,
    toggleTheme,
    toggleMotion,
    setTheme,
    setMotion,
    setLanguage,
    detectOSDarkMode,
    detectOSReducedMotion,
    syncWithDatabase,
    getStoredTheme,
    storeTheme,
    clearStoredTheme,
    getStoredMotion,
    storeMotion,
    clearStoredMotion,
    getStoredLanguage,
    storeLanguage,
    clearStoredLanguage
  }
})
